import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { toast } from 'react-toastify';
import type { Geometry } from 'geojson';
import dayjs from 'dayjs';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import CurrentReportsSitrepCard from 'components/Reports/CurrentReportsSitrepCard';
import SitrepToggleSection from 'components/Reports/SitrepToggleSection';
import type { GetSitrepsResponse } from 'components/Reports/reports.types';
import { NULL_VALUE } from 'constants/displayConstants';
import { useOptionalIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { useExportedFormFiles } from 'components/FileManagement/hooks/useExportedFormFiles';
import { useSignedUrl } from 'components/FileManagement/hooks/useNotebookQueries';
import useDownloadFile from 'components/FileManagement/hooks/useDownloadFile';
import { useAorText } from 'utils/aorTerminology';

function DetailRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <p className="tw:m-0 tw:text-foreground">
      <span className="tw:font-medium">{label}:</span> {children}
    </p>
  );
}

export type IncidentListItemData = {
  id: string;
  name: string;
  aor: string;
  incidentTypes: string[];
  aorIds: string[];
  relatedEventNames: string[];
  createdAt: string;
  latLong: { latitude: number; longitude: number } | null;
  locationGeometry: Geometry | null;
  currentPeriodId?: string;
};

type IncidentListItemProps = {
  incident: IncidentListItemData;
  currentOperationalPeriod: string;
  currentPeriodNumber: number;
  incidentCommander: string;
  sitrep: GetSitrepsResponse | null;
  showWorkspaceButton?: boolean;
  onWorkspaceClick?: () => void;
  /** When set, Incident Workspace stays visible and does not open the incident. */
  workspaceLockTooltip?: string;
};

export const IncidentListItem: React.FC<IncidentListItemProps> = ({
  incident,
  currentOperationalPeriod,
  currentPeriodNumber,
  incidentCommander,
  sitrep,
  showWorkspaceButton = false,
  onWorkspaceClick,
  workspaceLockTooltip,
}) => {
  const aorText = useAorText();
  const icsFormPdf = useOptionalIcsFormPdfPreview();
  const { data: exportedForms = [] } = useExportedFormFiles(incident.id);
  const { mutateAsync: getSignedUrl } = useSignedUrl();
  const downloadFile = useDownloadFile();

  const approvedPlanFile = useMemo(() => {
    // Period 0 is the only period without an IAP; its approved plan is the ICS-201. This used to also cover period 1,
    // because escalation created period 1 before any planning cycle had produced an IAP for it. Escalation now leaves
    // the incident on period 0, so by the time period 1 is current its IAP has already been generated.
    const isInitialResponsePeriod = currentPeriodNumber === 0;

    if (isInitialResponsePeriod) {
      const regex201 = /ICS[-_ ]?201/i;
      const regexOp0 = /OP[-_ ]?0/i;
      return exportedForms
        .filter(
          (file) =>
            regex201.test(file.file_name) && regexOp0.test(file.file_name)
        )
        .sort((a, b) => dayjs(b.updatedAt).diff(a.updatedAt))
        .at(0);
    }

    const exportedIAPPattern = new RegExp(
      '^IAP-OP' + currentPeriodNumber + '-'
    );
    return exportedForms
      .filter((file) => file.file_name.match(exportedIAPPattern))
      .sort((a, b) => dayjs(b.updatedAt).diff(a.updatedAt))
      .at(0);
  }, [currentPeriodNumber, exportedForms]);

  const handlePreviewActiveDocument = async () => {
    if (!icsFormPdf) return;

    if (!approvedPlanFile) {
      toast.info('No approved plan found for this operational period.');
      return;
    }

    try {
      const signedUrl = await getSignedUrl(approvedPlanFile.fileUrl);
      icsFormPdf.previewFromSignedUrl({
        fileUrl: signedUrl,
        fileName: approvedPlanFile.file_name,
        onDownload: () => {
          void downloadFile(approvedPlanFile);
        },
      });
    } catch {
      toast.error('Failed to load approved plan preview.');
    }
  };

  return (
    <div className="tw:space-y-2">
      {showWorkspaceButton && onWorkspaceClick ? (
        workspaceLockTooltip ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger
                render={
                  <span className="tw:inline-flex tw:cursor-not-allowed" />
                }
              >
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="tw:pointer-events-none tw:opacity-60"
                  tabIndex={-1}
                >
                  Incident Workspace
                </Button>
              </TooltipTrigger>
              <TooltipContent>{workspaceLockTooltip}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onWorkspaceClick}
          >
            Incident Workspace
          </Button>
        )
      ) : null}

      <div className="tw:space-y-1.5 tw:text-xs tw:text-foreground">
        <DetailRow label="Incident Categories">
          {incident.incidentTypes.join(', ') || NULL_VALUE}
        </DetailRow>
        <DetailRow label={aorText('AOR')}>{incident.aor}</DetailRow>
        {incident.relatedEventNames.length > 0 ? (
          <DetailRow label="Related Events">
            {incident.relatedEventNames.join(', ')}
          </DetailRow>
        ) : null}
        <DetailRow label="Incident Creation Time">
          {incident.createdAt}
        </DetailRow>
        <DetailRow label="Current Operational Period">
          {currentOperationalPeriod}
        </DetailRow>
        <Button
          type="button"
          variant="link"
          size="sm"
          className="tw:h-auto tw:px-0 tw:text-xs"
          onClick={() => {
            void handlePreviewActiveDocument();
          }}
          disabled={!icsFormPdf}
        >
          Approved plan for this op period
        </Button>
        <DetailRow label="Incident Commander">{incidentCommander}</DetailRow>
      </div>

      {sitrep ? (
        <SitrepToggleSection>
          <div className="tw:overflow-hidden">
            <CurrentReportsSitrepCard sitrep={sitrep} />
          </div>
        </SitrepToggleSection>
      ) : null}
    </div>
  );
};
